// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'

import { GridRowParams, GridRowsProp } from '@mui/x-data-grid-premium'
import {
  fetchDailyPoolAccountCTOs,
  fetchDailyPoolOverviewCTOs,
  fetchDailyVaultAccountCTOs,
  fetchPoolEventCTOs,
} from '@react/fetchers/PoolFetchers'

import { PoolDynamicAccountCTOSchema } from '@shared/PoolDynamicAccountCTO'
import { PoolDynamicOverviewCTOSchema } from '@shared/PoolDynamicCTO'
import { PoolEventCTO, PoolEventSchema } from '@shared/PoolEventCTO'
import { PoolFlexAccountCTOSchema } from '@shared/PoolFlexAccountCTO'
import { PoolFlexOverviewCTOSchema } from '@shared/PoolFlexCTO'
import { PoolLytAccountCTOSchema } from '@shared/PoolLytAccountCTO'
import { PoolLytOverviewCTOSchema } from '@shared/PoolLytOverviewCTO'
import { PoolTermAccountCTOSchema } from '@shared/PoolTermAccountCTO'
import { PoolOverviewCTOSchema } from '@shared/PoolTermCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { LytEventSchema } from '@shared/lytEventCTO'
import { WithdrawEventTypes } from '@shared/templateApx'
import { ZeroAddress } from 'ethers'

import { fetchChainTimestampDelta } from '@react/chainThunks/fetchChainTimestampDeltaTx'
import { fetchDefiExchangeRateHistory, fetchDefiFeeAccrualHistory } from '@react/fetchers/defiFetchers'
import { fetchTickets } from '@react/fetchers/ticketFetcher'
import { fetchLytEvents, fetchLytPoolReserve } from '@react/lyt/LytFetchers'
import { ChainId, PoolTypeEnum, ReportCategory, ReportDataShape, TimeStep } from '@shared/Enums'
import { FeeAccrualCTO, FeeAccrualCTOSchema } from '@shared/FeeAccrualCTO'
import { LytReservesSchema } from '@shared/LytReservesCTO'
import { getSwapTokenConfig } from '@shared/SwapTokenConfig'
import { TicketCTO, TicketCTOSchema, TicketStatusEnum } from '@shared/TicketCTO'
import { VaultAccountCTOSchemaNoRewards } from '@shared/VaultAccountCTO'
import { DefiExchangeRateCTO, DefiExchangeRateCTOSchema } from '@shared/defiCTO'
import { VaultAccountCTOSchema } from '@shared/sharedTypes'
import { formatAgeFromTimestamp } from '@shared/sharedUtils'
import { Dayjs } from 'dayjs'
import { JsonRpcProvider } from 'ethers'
import { ReactElement } from 'react'

export interface ReportFetchParams {
  serverApiBaseUrl: string
  restAPIKey: string
  poolAddr?: EthAddr
  lenderAddr?: EthAddr
  startDayStr?: string
  endDayStr?: string
  providerReadOnly?: JsonRpcProvider
  stageName?: string
  endDateTime?: Dayjs
  timeStep?: TimeStep
}
export enum ReportSelectorType {
  POOL_ADDR = 'POOL_ADDR',
  LENDER_ADDR = 'LENDER_ADDR',
  DATE_RANGE = 'DATE_RANGE',
  REPORT_DEFINITION = 'REPORT_DEFINITION',
  LYT_FILTERS = 'LYT_FILTERS',
  END_DATE_TIME = 'END_DATE_TIME',
  TIME_STEP = 'TIME_STEP',
}
export enum ReportSelectorRequirement {
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
  REQUIRED_OR_ALL = 'REQUIRED_OR_ALL',
}
export interface ReportDescriptor {
  label: string
  key: string
  category: ReportCategory
  selectors: Partial<Record<ReportSelectorType, ReportSelectorRequirement>>
  dataShape: ReportDataShape
  schema: yup.ObjectSchema<any>
  poolTypeFilter?: PoolTypeEnum

  fetcher: (params: ReportFetchParams) => Promise<GridRowsProp>
  actionColumns?: (params: GridRowParams<any>) => readonly ReactElement[]
  logoProp?: string
  companyNameProp?: string
}

export const REPORT_SIERRA_RESERVES: ReportDescriptor = {
  label: 'Reserves',
  key: 'RESERVES',
  category: ReportCategory.RESERVES,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.SIERRA_RESERVES,
  schema: LytReservesSchema,
  fetcher: async (params: ReportFetchParams) => {
    if (!params.poolAddr) {
      console.error('REPORT_SIERRA_RESERVES: No pool address provided')
      return []
    }
    const lytReservesCTOAllocation = await fetchLytPoolReserve(
      params.serverApiBaseUrl,
      params.restAPIKey,
      params.poolAddr
    )

    return lytReservesCTOAllocation.reserves
  },
}

export const REPORT_SIERRA_POOL_EVENTS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',
  category: ReportCategory.TRANSACTION_LEDGER,
  selectors: {
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.LYT_FILTERS]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.SIERRA_EVENTS,
  schema: LytEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { serverApiBaseUrl, restAPIKey, poolAddr, lenderAddr, startDayStr, endDayStr, providerReadOnly, stageName } =
      params

    // Build the params object for fetchLytEvents
    const fetchParams: any = { vaultAddress: poolAddr }
    if (lenderAddr) fetchParams.walletAddress = lenderAddr
    if (startDayStr) fetchParams.startDayStr = startDayStr
    if (endDayStr) fetchParams.endDayStr = endDayStr

    const poolEvents = await fetchLytEvents(serverApiBaseUrl, restAPIKey, fetchParams, providerReadOnly!)

    const swapEvents: any[] = []

    try {
      const network = await providerReadOnly?.getNetwork()
      const chainId = Number(network?.chainId) || ChainId.AVALANCHE
      const effectiveStageName = stageName || 'qasierra'
      const swapConfig = getSwapTokenConfig(effectiveStageName, chainId)
      const tokenA = swapConfig.TOKEN_A
      const tokenB = swapConfig.TOKEN_B
      const chainTimestampDelta = await fetchChainTimestampDelta(providerReadOnly!)

      // Use the existing swapEvents endpoint with toToken/fromToken parameters
      const swapUrl = `${serverApiBaseUrl}/swapEvents?toToken=${tokenA.address}&fromToken=${tokenB.address}&chainId=${chainId}`

      const response = await fetch(swapUrl, {
        headers: { 'x-api-key': restAPIKey },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch swap events: ${response.status}`)
      }

      const uniqueSwaps = await response.json()

      if (uniqueSwaps.length > 0) {
        const addressSwapEvents = uniqueSwaps.map((swap: any) => {
          const swapTimestamp = parseInt(swap.time) || swap.timestamp
          const swapEvent = {
            id: swap.id,
            eventName: 'Swap',
            transactionHash: swap.txHashUrl || '',
            blockNumber: 0,
            timestamp: swapTimestamp,
            fromAddr: swap.userAddress,
            toAddr: swap.userAddress,
            assets: parseFloat(swap.toAmount) * 1e6 || 0,
            assetsDisplay: `${swap.fromAmount} ${swap.fromTokenSymbol} → ${swap.toAmount} ${swap.toTokenSymbol}`,
            shares: '',
            poolAddr: '0x0000000000000000000000000000000000000000',
            poolName: 'Swap',
            accountAddr: swap.userAddress,
            currency: swap.toTokenSymbol,
            symbol: '',
            liquidityTokenSymbol: swap.toTokenSymbol,
            chainId: parseInt(swap.chainIndex) || ChainId.AVALANCHE,
            ageString: formatAgeFromTimestamp(swapTimestamp, chainTimestampDelta),
            timestampDateString: '',
            timestampString: '',
            fees: '0',
            exchangeRate: '1',
            eventType: 'Swap',
            fromAmount: swap.fromAmount,
            toAmount: swap.toAmount,
            fromTokenSymbol: swap.fromTokenSymbol,
            toTokenSymbol: swap.toTokenSymbol,
          }

          return swapEvent
        })
        swapEvents.push(...addressSwapEvents)
      }
    } catch (error) {
      console.warn('Failed to fetch swap events:', error)
    }

    return [...poolEvents, ...swapEvents]
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_EVENTS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',
  category: ReportCategory.TRANSACTION_LEDGER,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED_OR_ALL,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.OPTIONAL,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr || poolAddr == ZeroAddress) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_DYNAMIC: ReportDescriptor = {
  label: 'Dynamic - Overview',
  key: 'POOL_OVERVIEW_DYNAMIC',
  category: ReportCategory.POOL_OVERVIEW,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Dynamic,
  dataShape: ReportDataShape.POOL_OVERVIEW_DYNAMIC,
  schema: PoolDynamicOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_FLEX: ReportDescriptor = {
  label: 'Flexible Term - Overview',
  key: 'POOL_OVERVIEW_FLEX',
  category: ReportCategory.POOL_OVERVIEW,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.FlexibleRate,
  dataShape: ReportDataShape.POOL_OVERVIEW_FLEX,
  schema: PoolFlexOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_TERM: ReportDescriptor = {
  label: 'Fixed Term - Overview',
  key: 'POOL_OVERVIEW_Term',
  category: ReportCategory.POOL_OVERVIEW,

  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Term,
  dataShape: ReportDataShape.POOL_OVERVIEW_TERM,
  schema: PoolOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_LYT: ReportDescriptor = {
  label: 'LYT - Overview',
  key: 'POOL_OVERVIEW_LYT',
  category: ReportCategory.POOL_OVERVIEW,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Lyt,
  dataShape: ReportDataShape.POOL_OVERVIEW_LYT,
  schema: PoolLytOverviewCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchDailyPoolOverviewCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_DYNAMIC: ReportDescriptor = {
  label: 'Dynamic - Daily & Summary',
  key: 'POOL_ACCOUNT_DYNAMIC',
  category: ReportCategory.LENDER_TIME_PERIOD,
  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Dynamic,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC,
  schema: PoolDynamicAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_FLEX: ReportDescriptor = {
  label: 'Flexible Term - Daily & Summary',
  key: 'POOL_ACCOUNT_FLEX',
  category: ReportCategory.LENDER_TIME_PERIOD,

  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.FlexibleRate,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_FLEX,
  schema: PoolFlexAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}
export const REPORT_DESCRIPTOR_VAULT_ACCOUNT_NO_REWARDS: ReportDescriptor = {
  label: 'Balances: Daily & Summary',
  key: 'VAULT_ACCOUNT',
  category: ReportCategory.LENDER_TIME_PERIOD,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED_OR_ALL,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.OPTIONAL,
  },
  dataShape: ReportDataShape.VAULT_ACCOUNT,
  schema: VaultAccountCTOSchemaNoRewards,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyVaultAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_DESCRIPTOR_VAULT_ACCOUNT: ReportDescriptor = {
  label: 'Balances: Daily & Summary',
  key: 'VAULT_ACCOUNT',
  category: ReportCategory.LENDER_TIME_PERIOD,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED_OR_ALL,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.OPTIONAL,
  },
  dataShape: ReportDataShape.VAULT_ACCOUNT,
  schema: VaultAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyVaultAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_TERM: ReportDescriptor = {
  label: 'Fixed Term - Daily & Summary',
  key: 'POOL_ACCOUNT_TERM',
  category: ReportCategory.LENDER_TIME_PERIOD,
  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Term,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_TERM,
  schema: PoolTermAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_LYT: ReportDescriptor = {
  label: 'LYT - Daily & Summary',
  key: 'POOL_ACCOUNT_LYT',
  category: ReportCategory.LENDER_TIME_PERIOD,
  selectors: {
    [ReportSelectorType.LENDER_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.REPORT_DEFINITION]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: PoolTypeEnum.Lyt,
  dataShape: ReportDataShape.LENDER_TIME_PERIOD_LYT,
  schema: PoolLytAccountCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && lenderAddr) {
      const poolEvents = await fetchDailyPoolAccountCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      return poolEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_WITHDRAW_EVENTS_ALL_LENDERS: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',
  category: ReportCategory.TRANSACTION_LEDGER,
  selectors: {},
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  companyNameProp: 'accountAddr',
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )
      const withdrawEvents: PoolEventCTO[] = poolEvents.filter((event: PoolEventCTO) => {
        return WithdrawEventTypes.includes(event.eventType)
      })
      return withdrawEvents
    }
    return []
  },
}

export const REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST: ReportDescriptor = {
  label: 'Transaction Ledger',
  key: 'POOL_EVENTS',
  category: ReportCategory.TRANSACTION_LEDGER,
  selectors: {
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, lenderAddr, startDayStr, endDayStr, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr) {
      const poolEvents = await fetchPoolEventCTOs(
        serverApiBaseUrl,
        restAPIKey,
        poolAddr,
        lenderAddr,
        startDayStr,
        endDayStr
      )

      return poolEvents
    }

    return []
  },
}

export const REPORT_DEFI_EXCHANGE_RATE_HISTORY: ReportDescriptor = {
  label: 'Defi Exchange Rate History',
  key: 'DEFI_EXCHANGE_RATE',
  category: ReportCategory.DEFI_EXCHANGE_RATE,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.END_DATE_TIME]: ReportSelectorRequirement.OPTIONAL,
    [ReportSelectorType.TIME_STEP]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.DEFI_EXCHANGE_RATE,
  schema: DefiExchangeRateCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    const { poolAddr, endDateTime, timeStep, serverApiBaseUrl, restAPIKey } = params

    if (poolAddr && endDateTime && timeStep) {
      const defiExchangeRateHistory = await fetchDefiExchangeRateHistory(
        poolAddr,
        timeStep,
        endDateTime,
        serverApiBaseUrl,
        restAPIKey
      )

      return defiExchangeRateHistory.map((defiExchangeRateCTO: DefiExchangeRateCTO, index: number) => {
        return {
          id: index,
          ...defiExchangeRateCTO,
        }
      })
    }

    return []
  },
}

export const REPORT_DEFI_FEE_ACCRUAL_HISTORY: ReportDescriptor = {
  label: 'Defi Fee Accrual History',
  key: 'DEFI_FEE_ACCRUAL',
  category: ReportCategory.DEFI_FEE_ACCRUAL,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED,
    [ReportSelectorType.END_DATE_TIME]: ReportSelectorRequirement.OPTIONAL,
    [ReportSelectorType.TIME_STEP]: ReportSelectorRequirement.OPTIONAL,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.DEFI_FEE_ACCRUAL,
  schema: FeeAccrualCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    try {
      const { poolAddr, endDateTime, timeStep, serverApiBaseUrl, restAPIKey } = params

      if (poolAddr && endDateTime && timeStep) {
        const feeAccrualCTOs = await fetchDefiFeeAccrualHistory(
          poolAddr,
          timeStep,
          endDateTime,
          serverApiBaseUrl,
          restAPIKey
        )

        return feeAccrualCTOs.map((defiFeeAccrualCTO: FeeAccrualCTO, index: number) => {
          return {
            id: index,
            ...defiFeeAccrualCTO,
          }
        })
      }
    } catch (error) {
      console.error('error fetching defi fee accrual history', error)
      return []
    }

    return []
  },
}

export const REPORT_TICKET_PENDING: ReportDescriptor = {
  label: 'Tickets Pending',
  key: 'TICKETS_PENDING',
  category: ReportCategory.TICKETS_PENDING,
  selectors: {},
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.TICKETS_PENDING,
  schema: TicketCTOSchema,
  fetcher: async (params: ReportFetchParams) => {
    try {
      const { serverApiBaseUrl, restAPIKey, poolAddr } = params

      if (serverApiBaseUrl) {
        const ticketsPending = await fetchTickets(serverApiBaseUrl, restAPIKey, TicketStatusEnum.PENDING, poolAddr)

        return ticketsPending ?? ([] as TicketCTO[])
      }
    } catch (error) {
      console.error('error fetching tickets pending', error)
      return []
    }

    return []
  },
}

export const REPORT_DESCRIPTOR_UNSELECTED: ReportDescriptor = {
  label: '---- Select Report Type ----',
  key: 'UNSELECTED',
  category: ReportCategory.UNSELECTED,
  selectors: {
    [ReportSelectorType.POOL_ADDR]: ReportSelectorRequirement.REQUIRED_OR_ALL,
    [ReportSelectorType.DATE_RANGE]: ReportSelectorRequirement.REQUIRED,
  },
  poolTypeFilter: undefined,
  dataShape: ReportDataShape.POOL_EVENTS,
  schema: PoolEventSchema,
  fetcher: async (_params: ReportFetchParams) => {
    return []
  },
}
export const ADMIN_REPORT_DESCRIPTORS: ReportDescriptor[] = [
  REPORT_DESCRIPTOR_UNSELECTED,
  REPORT_DESCRIPTOR_VAULT_ACCOUNT,
  REPORT_FETCH_DESCRIPTOR_POOL_EVENTS,
  REPORT_DEFI_FEE_ACCRUAL_HISTORY,
  REPORT_DEFI_EXCHANGE_RATE_HISTORY,
  REPORT_TICKET_PENDING,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_DYNAMIC,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_FLEX,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_TERM,
  REPORT_FETCH_DESCRIPTOR_POOL_OVERVIEW_LYT,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_DYNAMIC,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_FLEX,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_TERM,
  REPORT_FETCH_DESCRIPTOR_POOL_ACCOUNT_LYT,
]

export const LENDER_REPORT_DESCRIPTORS: ReportDescriptor[] = [
  REPORT_DESCRIPTOR_UNSELECTED,
  REPORT_DESCRIPTOR_VAULT_ACCOUNT_NO_REWARDS,
  REPORT_FETCH_DESCRIPTOR_POOL_EVENTS,
]
